import { toLogicalPath, toPublicPath } from './localizedRoutes.shared.js';

export const DEFAULT_SITE_URL = 'https://figtures.com';
export const SUPPORTED_LOCALES = ['tr', 'en'];
/** English public URLs live at `/`; Turkish at `/tr/* with localized segments. */
export const DEFAULT_LOCALE = 'en';
/** hreflang x-default and primary market signal — Turkish canonical preference. */
export const SEO_X_DEFAULT_LOCALE = 'tr';
export const NON_DEFAULT_LOCALE_PATH_PREFIX = 'tr';
export const DEFAULT_LOCALE_URL_STRATEGY = 'none';

export { toLogicalPath, toPublicPath } from './localizedRoutes.shared.js';

export function normalizeSiteUrl(value) {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';

  const withProtocol = /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    return parsed.origin;
  } catch {
    return '';
  }
}

export function resolveSiteUrl(envSiteUrl) {
  return normalizeSiteUrl(envSiteUrl) || DEFAULT_SITE_URL;
}

export function normalizeLocaleUrlStrategy(strategy) {
  return strategy === 'query' || strategy === 'path' ? strategy : DEFAULT_LOCALE_URL_STRATEGY;
}

export function normalizeCanonicalPath(pathname) {
  if (typeof pathname !== 'string') return '/';

  const trimmed = pathname.trim();
  if (!trimmed) return '/';

  try {
    if (/^https?:\/\//i.test(trimmed)) {
      const parsed = new URL(trimmed);
      let pathOnly = parsed.pathname || '/';
      if (pathOnly.length > 1 && pathOnly.endsWith('/')) {
        pathOnly = pathOnly.replace(/\/+$/, '') || '/';
      }
      const pathWithQuery = `${pathOnly}${parsed.search || ''}`;
      return pathWithQuery.startsWith('/') ? pathWithQuery : `/${pathWithQuery}`;
    }
  } catch {
    // ignore and continue with raw path normalization
  }

  let path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  if (path.length > 1 && path.endsWith('/')) {
    path = path.replace(/\/+$/, '') || '/';
  }
  return path;
}

export function buildAbsoluteUrl(siteUrl, pathname) {
  const origin = resolveSiteUrl(siteUrl);
  const canonicalPath = normalizeCanonicalPath(pathname);

  if (canonicalPath === '/') return origin;
  return `${origin}${canonicalPath}`;
}

function withLocalePrefix(pathname, locale, defaultLocale) {
  return toPublicPath(pathname, locale, defaultLocale);
}

function withLangQuery(pathname, locale) {
  const [basePath, hash = ''] = pathname.split('#');
  const [pathOnly, queryString = ''] = basePath.split('?');
  const params = new URLSearchParams(queryString);
  params.set('lang', locale);
  const query = params.toString();
  const withQuery = query ? `${pathOnly}?${query}` : pathOnly;
  return hash ? `${withQuery}#${hash}` : withQuery;
}

function stripPathQueryHash(pathname) {
  return (pathname || '/').split('?')[0].split('#')[0] || '/';
}

/** Removes `/tr` prefix and TR URL segments → English logical path for SEO metadata lookup. */
export function stripLocalePrefixFromPath(pathname) {
  return toLogicalPath(pathname);
}

/** Detect locale from URL path (`/` → en, `/tr` → tr). */
export function detectLocaleFromPathname(pathname, defaultLocale = DEFAULT_LOCALE) {
  const base = stripPathQueryHash(normalizeCanonicalPath(pathname));
  if (base === '/tr' || base.startsWith('/tr/')) return 'tr';
  return defaultLocale === 'en' ? 'en' : 'tr';
}

export function expandLocalizedPublicPaths(
  paths,
  {
    localeUrlStrategy = DEFAULT_LOCALE_URL_STRATEGY,
    supportedLocales = SUPPORTED_LOCALES,
    defaultLocale = DEFAULT_LOCALE,
  } = {},
) {
  const strategy = normalizeLocaleUrlStrategy(localeUrlStrategy);
  if (strategy !== 'path') return [...paths];

  const expanded = new Set();
  for (const logicalPath of paths) {
    for (const locale of supportedLocales) {
      expanded.add(
        buildLocaleAwarePath({
          pathname: logicalPath,
          locale,
          localeUrlStrategy: strategy,
          defaultLocale,
        }),
      );
    }
  }
  return [...expanded].sort((a, b) => a.localeCompare(b));
}

export function buildLocaleAwarePath({ pathname, locale, localeUrlStrategy, defaultLocale }) {
  if (localeUrlStrategy === 'query') {
    return withLangQuery(pathname, locale);
  }

  if (localeUrlStrategy === 'path') {
    return withLocalePrefix(pathname, locale, defaultLocale);
  }

  return pathname;
}

export function buildSeoUrls({
  siteUrl,
  canonicalPath,
  locale = DEFAULT_LOCALE,
  supportedLocales = SUPPORTED_LOCALES,
  defaultLocale = DEFAULT_LOCALE,
  xDefaultLocale = SEO_X_DEFAULT_LOCALE,
  localeUrlStrategy = DEFAULT_LOCALE_URL_STRATEGY,
}) {
  const normalizedPath = normalizeCanonicalPath(canonicalPath);
  const strategy = normalizeLocaleUrlStrategy(localeUrlStrategy);

  const canonicalLocalePath = buildLocaleAwarePath({
    pathname: normalizedPath,
    locale,
    localeUrlStrategy: strategy,
    defaultLocale,
  });

  const canonicalUrl = buildAbsoluteUrl(siteUrl, canonicalLocalePath);

  const alternateLinks =
    strategy === 'none'
      ? []
      : supportedLocales.map((supportedLocale) => {
          const localizedPath = buildLocaleAwarePath({
            pathname: normalizedPath,
            locale: supportedLocale,
            localeUrlStrategy: strategy,
            defaultLocale,
          });

          return {
            hrefLang: supportedLocale,
            href: buildAbsoluteUrl(siteUrl, localizedPath),
          };
        });

  const xDefaultUrl =
    strategy === 'none'
      ? undefined
      : buildAbsoluteUrl(
          siteUrl,
          buildLocaleAwarePath({
            pathname: normalizedPath,
            locale: xDefaultLocale,
            localeUrlStrategy: strategy,
            defaultLocale,
          }),
        );

  return {
    canonicalUrl,
    alternateLinks,
    xDefaultUrl,
  };
}

/**
 * Normalizes CMS timestamps to sitemap lastmod (YYYY-MM-DD).
 * @param {unknown} value
 * @param {string} fallbackDate ISO date slice(0,10)
 */
export function formatSitemapLastmod(value, fallbackDate) {
  if (!value) return fallbackDate;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallbackDate;
  return date.toISOString().slice(0, 10);
}

/** Latest valid YYYY-MM-DD for sitemap lastmod (ignores empty / invalid). */
export function maxSitemapLastmod(...candidates) {
  let best = '';
  for (const raw of candidates) {
    if (!raw || typeof raw !== 'string') continue;
    const slice = raw.trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(slice)) continue;
    if (!best || slice > best) best = slice;
  }
  return best;
}

/**
 * Repo-relative paths whose latest git commit should drive lastmod for a logical route.
 * Empty array = skip git (blog/project detail use CMS or catalog dates).
 * Detail routes list only their content catalogs: a shared detail template change must not bump every URL.
 */
export function resolveSitemapRouteSourcePaths(route) {
  if (route.startsWith('/blog/') && route !== '/blog' && !route.startsWith('/blog/tag/')) {
    return [];
  }
  if (route.startsWith('/projects/')) {
    return ['src/lib/portfolioProjects.ts'];
  }
  if (route.startsWith('/services/')) {
    return ['src/lib/serviceCatalog.ts'];
  }
  if (route === '/services') {
    return ['src/lib/serviceCatalog.ts', 'src/pages/ServicesPage.tsx'];
  }
  if (route.startsWith('/solutions/')) {
    return ['src/lib/solutionCatalog.ts'];
  }
  if (route === '/solutions') {
    return ['src/lib/solutionCatalog.ts', 'src/pages/SolutionsPage.tsx'];
  }
  if (route.startsWith('/playbooks/')) {
    return ['src/lib/authorityAssetCatalog.ts'];
  }
  if (route === '/playbooks') {
    return ['src/lib/authorityAssetCatalog.ts', 'src/pages/PlaybooksPage.tsx'];
  }
  if (route.startsWith('/guides/')) {
    return ['src/lib/pillarGuideCatalog.ts'];
  }
  if (route === '/guides') {
    return ['src/lib/pillarGuideCatalog.ts', 'src/pages/GuidesPage.tsx'];
  }
  if (route.startsWith('/ai-answers/')) {
    return ['src/lib/aiAnswersCatalog.ts'];
  }
  if (route === '/ai-answers') {
    return ['src/lib/aiAnswersCatalog.ts', 'src/pages/AIAnswersPage.tsx'];
  }
  if (route.startsWith('/tools/')) {
    return ['src/data/tools.data.mjs', 'src/data/toolsLocale.ts'];
  }
  if (route === '/tools') {
    return ['src/data/tools.data.mjs', 'src/pages/ToolsPage.tsx'];
  }
  if (route.startsWith('/blog/tag/')) {
    // Lastmod = newest tagged post (generate-sitemap.mjs applyBlogTagLastmod), not a catalog commit.
    return [];
  }
  if (route === '/blog') {
    return ['src/pages/BlogPage.tsx', 'src/lib/blogData.ts', 'src/lib/blogDataCommercial.ts'];
  }
  if (route === '/projects') {
    return ['src/pages/ProjectsPage.tsx', 'src/lib/portfolioProjects.ts'];
  }
  const staticPages = {
    '/': ['src/pages/Index.tsx'],
    '/about': ['src/pages/AboutPage.tsx'],
    '/linkedin': ['src/pages/LinkedInCompanyPage.tsx'],
    '/contact': ['src/pages/ContactPage.tsx', 'src/components/ContactSection.tsx'],
    '/privacy': ['src/pages/PrivacyPage.tsx'],
    '/terms': ['src/pages/TermsPage.tsx'],
    '/cookie-policy': ['src/pages/CookiePolicyPage.tsx'],
    '/kvkk-aydinlatma-metni': ['src/pages/KvkkPage.tsx'],
    '/delivery-cancellation-refund': ['src/pages/DeliveryRefundPage.tsx'],
    '/distance-sales-agreement': ['src/pages/DistanceSalesPage.tsx'],
  };
  return staticPages[route] ?? [];
}

/**
 * @param {unknown} dynamicRoutesPayload parsed generated-dynamic-routes.json
 */
export function extractLastmodByPathFromDynamicRoutesPayload(dynamicRoutesPayload) {
  const lastmodByPath = dynamicRoutesPayload?.lastmodByPath;
  return lastmodByPath && typeof lastmodByPath === 'object' ? { ...lastmodByPath } : {};
}

/** Merges persisted lastmod with fresh blog/project dates from Supabase. */
export function mergeLastmodByPath(previousLastmodByPath, dynamicLastmodByPath) {
  return { ...previousLastmodByPath, ...dynamicLastmodByPath };
}

/** Static blog/project slug: reuse prior lastmod so deploy alone does not bump unchanged content. */
export function lastmodForPersistedContentPath(path, previousLastmodByPath, buildDate) {
  return previousLastmodByPath[path] || buildDate;
}

/** Any sitemap logical route: explicit content lastmod or build date fallback. */
export function lastmodForSitemapRoute(route, mergedLastmodByPath, buildDate) {
  const explicit = mergedLastmodByPath[route];
  if (explicit) return explicit;
  return buildDate;
}
